import type { SyncState } from './engine';
import type { AuthState } from './auth';

/**
 * One honest sentence about where the cloud stands. "synced" is only ever
 * produced when the engine has no pending or failed document.
 */

export type SummaryTone = 'quiet' | 'working' | 'attention';

export interface CloudSummary {
  state: 'off' | 'loading' | 'signed-out' | 'expired' | 'recovery' | 'denied' | 'synced' | 'syncing' | 'pending' | 'offline' | 'error' | 'blocked';
  label: string;
  /** A second line, when there is something useful to add. */
  detail: string | null;
  tone: SummaryTone;
  /** Documents the cloud does not hold the latest text of. */
  waiting: number;
}

export function relativeTime(then: number, now: number): string {
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 10) return 'just now';
  if (s < 60) return `${s} seconds ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} ${m === 1 ? 'minute' : 'minutes'} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ${h === 1 ? 'hour' : 'hours'} ago`;
  const d = Math.round(h / 24);
  return `${d} ${d === 1 ? 'day' : 'days'} ago`;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function summarize(auth: Pick<AuthState, 'status' | 'expired'>, sync: SyncState | null, options: { autosave: boolean; configured: boolean; now?: number }): CloudSummary {
  const make = (state: CloudSummary['state'], label: string, tone: SummaryTone, detail: string | null = null, waiting = 0): CloudSummary => ({ state, label, detail, tone, waiting });
  if (!options.configured || auth.status === 'off') return make('off', 'Cloud isn’t set up in this copy of MyTXT', 'quiet');
  if (auth.status === 'loading') return make('loading', 'Checking your account…', 'working');
  if (auth.status === 'recovery') return make('recovery', 'Choose a new password to continue', 'attention');
  if (auth.status === 'signed-out') {
    return auth.expired ? make('expired', 'Your session ended. Sign in to resume syncing.', 'attention', 'Your writing is safe on this device.') : make('signed-out', 'Not signed in', 'quiet', 'Documents stay on this device.');
  }
  if (!sync) return make('loading', 'Connecting…', 'working');
  const waiting = Object.values(sync.docs).filter((s) => s !== 'synced').length;
  if (sync.access === 'denied') return make('denied', 'This account isn’t enabled for cloud storage', 'attention', 'MyTXT cloud is in a private beta. Your writing stays on this device.');
  if (sync.blocked === 'auth') return make('blocked', 'Sign in again to keep syncing', 'attention', 'Your writing is safe on this device.', waiting);
  if (!sync.online) return make('offline', 'Offline', 'quiet', waiting ? `${plural(waiting, 'document', 'documents')} will sync when you’re back online.` : 'Changes will sync when you’re back online.', waiting);
  if (sync.inflight > 0) return make('syncing', 'Syncing…', 'working', null, waiting);
  if (waiting > 0) {
    const failed = Object.values(sync.docs).some((s) => s === 'error' || s === 'blocked');
    if (failed) return make('error', sync.error ?? 'Some changes haven’t synced', 'attention', `${plural(waiting, 'document', 'documents')} waiting. Your writing is safe on this device.`, waiting);
    if (!options.autosave) return make('pending', `${plural(waiting, 'document', 'documents')} not synced`, 'attention', 'Cloud Autosave is off. Use Sync Now to upload.', waiting);
    return make('pending', `${plural(waiting, 'change', 'changes')} waiting to sync`, 'working', null, waiting);
  }
  const at = sync.lastSyncedAt;
  return make('synced', 'Everything is saved to the cloud', 'quiet', at ? `Last synced ${relativeTime(at, options.now ?? Date.now())}` : null);
}
